import { Reducer } from 'redux';

import { Nullable } from '@common/typescript/objects/Nullable';
import { List } from '@common/typescript/objects/List';
import { WithId } from '@common/typescript/objects/WithId';

import { LocalizationEntry } from '@app/objects/LocalizationEntry';
import { AppThunkAction, ApplicationState } from '@app/store/index';
import { request } from '@app/components/Api';

export interface LocalizationEntriesRecord {
	map: Map<string, LocalizationEntry>;
	state: 'querying' | 'mutating' | 'idle';
	error: Nullable<string>;
	controller?: AbortController;
}

function getDefaultRecord(): LocalizationEntriesRecord {
	return {
		map: new Map<string, LocalizationEntry>(),
		state: 'idle',
		error: null,
		controller: undefined,
	};
}

export type LocalizationEntriesState = Partial<Record<number, LocalizationEntriesRecord>>;

export enum ActionKeys {
	REQUESTLOCALIZATIONENTRIES = 'REQUESTLOCALIZATIONENTRIES',
	RECEIVELOCALIZATIONENTRIES = 'RECEIVELOCALIZATIONENTRIES',
	RECEIVELOCALIZATIONENTRIESERROR = 'RECEIVELOCALIZATIONENTRIESERROR',
	MUTATEDLOCALIZATIONENTRIES = 'MUTATEDLOCALIZATIONENTRIES',
}

interface PayloadBase {
	crematoryId: number;
}

interface RequestPayload extends PayloadBase {
	controller: AbortController;
}

interface RequestAction {
	type: ActionKeys.REQUESTLOCALIZATIONENTRIES;
	payload: RequestPayload;
}

interface ReceiveErrorPayload extends PayloadBase {
	error: Nullable<string>;
}

interface ReceiveErrorAction {
	type: ActionKeys.RECEIVELOCALIZATIONENTRIESERROR;
	payload: ReceiveErrorPayload;
}

interface ReceiveListPayload extends PayloadBase {
	list: List<LocalizationEntry>;
}

interface ReceiveListAction {
	type: ActionKeys.RECEIVELOCALIZATIONENTRIES;
	payload: ReceiveListPayload;
}

interface MutatePayload extends PayloadBase {
	controller: AbortController;
}

interface MutateItemAction {
	type: ActionKeys.MUTATEDLOCALIZATIONENTRIES;
	payload: MutatePayload;
}

type KnownActions = RequestAction
	| ReceiveErrorAction
	| ReceiveListAction
	| MutateItemAction;

type ActionResult = AppThunkAction<KnownActions>;
type GetState = () => ApplicationState;
type Dispatch = (action: KnownActions) => void;

type InsertData = Omit<LocalizationEntry, 'id'> & { id: -1; deleted: never };
type UpdateData = Partial<LocalizationEntry> & WithId;
type DeleteData = WithId & { deleted: true };
interface MutateData extends PayloadBase {
	item: InsertData | UpdateData | DeleteData;
}

export interface ActionCreators {
	request: (force?: boolean) => ActionResult;
	mutate: (mutation: MutateData) => ActionResult;
}

const fetchParams = {
	count: 1000,
	offset: 0,
};

export function getActionCreators(crematoryId: number): ActionCreators {
	const createController = (getState: GetState): AbortController => {
		const state = getState();
		const record = state.localizationEntries[crematoryId];

		record?.controller?.abort();
		const controller = new AbortController();

		// This is a tricky thing. We should not mutate record directly
		// We do not need this field to rerender site.
		// But we do need any feature requests to cancel this one as soon as possible
		if (record) {
			record.controller = controller;
		}

		return controller;
	};

	const query = (force: boolean, dispatch: Dispatch, getState: GetState) => {
		const state = getState();
		const record = state.localizationEntries[crematoryId];

		if (!force && record) return Promise.resolve();

		const controller = createController(getState);

		dispatch({
			type: ActionKeys.REQUESTLOCALIZATIONENTRIES,
			payload: {
				crematoryId,
				controller,
			},
		});

		return request<List<LocalizationEntry>>('localizationEntryList', fetchParams, state, controller.signal)
			.then((list: List<LocalizationEntry>) => {
				dispatch({
					type: ActionKeys.RECEIVELOCALIZATIONENTRIES,
					payload: {
						crematoryId,
						list,
					},
				});
			})
			.catch((error: Error) => {
				dispatch({
					type: ActionKeys.RECEIVELOCALIZATIONENTRIESERROR,
					payload: {
						crematoryId,
						error: error?.message,
					},
				});
			});
	};

	return {
		request: (force: boolean = false): ActionResult => (dispatch: Dispatch, getState: GetState) => query(force, dispatch, getState),

		mutate(mutation: MutateData): ActionResult {
			return (dispatch: Dispatch, getState: GetState) => {
				const controller = createController(getState);
				const state = getState();

				dispatch({
					type: ActionKeys.MUTATEDLOCALIZATIONENTRIES,
					payload: {
						crematoryId,
						controller,
					},
				});

				let task: Promise<void>;

				if (mutation.item.id < 0) {
					task = request('createLocalizationEntry', mutation.item, state, controller.signal);
				} else if ((mutation.item as DeleteData).deleted) {
					task = request('deleteLocalizationEntry', mutation.item, state, controller.signal);
				} else {
					task = request('updateLocalizationEntry', mutation.item, state, controller.signal);
				}

				return task
					.then(() => query(true, dispatch, getState))
					.catch((error: Error) => {
						dispatch({
							type: ActionKeys.RECEIVELOCALIZATIONENTRIESERROR,
							payload: {
								crematoryId,
								error: error?.message,
							},
						});
					});
			};
		},
	};
}

function mutateState(state: LocalizationEntriesState, crematoryId: number, record: LocalizationEntriesRecord): LocalizationEntriesState {
	return {
		...state,
		[crematoryId]: record,
	};
}

function mutateRecord(source: LocalizationEntriesRecord | undefined, mutation: Partial<LocalizationEntriesRecord>): LocalizationEntriesRecord {
	const record = source ?? getDefaultRecord();

	return {
		...record,
		...mutation,
	};
}

function toMap(list: List<LocalizationEntry>): Map<string, LocalizationEntry> {
	return list.list.reduce(
		(acc: Map<string, LocalizationEntry>, cur: LocalizationEntry) => acc.set(cur.key, cur),
		new Map<string, LocalizationEntry>(),
	);
}

export function getReducer(): Reducer<LocalizationEntriesState, KnownActions> {
	return (state: LocalizationEntriesState = {}, action: KnownActions) => {
		switch (action.type) {
		case ActionKeys.REQUESTLOCALIZATIONENTRIES: {
			const record = state[action.payload.crematoryId];
			const mutation: Partial<LocalizationEntriesRecord> = {
				state: 'querying',
				controller: action.payload.controller,
			};
			const mutated = mutateRecord(record, mutation);

			return mutateState(state, action.payload.crematoryId, mutated);
		}

		case ActionKeys.RECEIVELOCALIZATIONENTRIESERROR: {
			const record = state[action.payload.crematoryId];
			const mutation: Partial<LocalizationEntriesRecord> = {
				state: 'idle',
				error: action.payload.error,
			};
			const mutated = mutateRecord(record, mutation);

			return mutateState(state, action.payload.crematoryId, mutated);
		}

		case ActionKeys.RECEIVELOCALIZATIONENTRIES: {
			const record = state[action.payload.crematoryId];
			const mutation: Partial<LocalizationEntriesRecord> = {
				state: 'idle',
				map: toMap(action.payload.list),
				error: null,
			};
			const mutated = mutateRecord(record, mutation);

			return mutateState(state, action.payload.crematoryId, mutated);
		}
		case ActionKeys.MUTATEDLOCALIZATIONENTRIES: {
			const record = state[action.payload.crematoryId];
			const mutation: Partial<LocalizationEntriesRecord> = {
				state: 'mutating',
				controller: action.payload.controller,
			};
			const mutated = mutateRecord(record, mutation);

			return mutateState(state, action.payload.crematoryId, mutated);
		}

		default: return state;
		}
	};
}